import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, Key, CheckCircle, XCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [tokenEmail, setTokenEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    // Validate token before showing the form (matches new /api/portal-auth contract)
    setValidating(true);
    axios.get(`${BACKEND_URL}/api/portal-auth/reset-password/${token}`)
      .then(({ data }) => {
        setTokenValid(true);
        setTokenEmail(data.email || '');
      })
      .catch((err) => {
        setTokenValid(false);
        toast.error(err.response?.data?.detail || 'Token inválido ou expirado');
      })
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('A password deve ter pelo menos 8 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('As passwords não coincidem');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portal-auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password alterada com sucesso!');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Erro ao alterar password');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <ShieldCheck className="w-12 h-12 text-[#10B981]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Privaxia
          </h1>
          <p className="text-sm text-slate-600">Plataforma de Gestão de Consentimentos RGPD</p>
        </div>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-[#10B981]" />
              Redefinir Password
            </CardTitle>
            <CardDescription>
              {tokenValid ? 'Introduza a sua nova password' : 'Verificar token de recuperação'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <div className="text-center py-6 space-y-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A]">Token em Falta</h3>
                  <p className="text-sm text-slate-600 mt-2">
                    Não foi fornecido um token de recuperação. 
                    Por favor, utilize o link enviado para o seu email.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/forgot-password')}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                >
                  Solicitar Novo Link
                </Button>
              </div>
            ) : !tokenValid ? (
              <div className="text-center py-6 space-y-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A]">Token Inválido ou Expirado</h3>
                  <p className="text-sm text-slate-600 mt-2">
                    O link de recuperação de password é inválido ou já expirou.
                    Por favor, solicite um novo link.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/forgot-password')}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                >
                  Solicitar Novo Link
                </Button>
              </div>
            ) : success ? (
              <div className="text-center py-6 space-y-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A]">Password Alterada!</h3>
                  <p className="text-sm text-slate-600 mt-2">
                    A sua password foi alterada com sucesso.
                    Já pode fazer login com a nova password.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/login-local')}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                >
                  Ir para Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {tokenEmail && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-600">
                      A alterar password para: <strong>{tokenEmail}</strong>
                    </p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="password">Nova Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="reset-password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Password</Label>
                  <Input
                    id="confirmPassword"
                    data-testid="reset-password-confirm-input"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repita a password"
                    required
                  />
                </div>
                
                <Button
                  data-testid="reset-password-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                >
                  {loading ? 'A alterar...' : 'Alterar Password'}
                </Button>
              </form>
            )}
            
            <div className="mt-6 pt-6 border-t border-slate-200">
              <Link
                to="/login-local"
                className="flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-[#10B981] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar ao Login
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          © 2026 Privaxia. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
